import { FOCUSABLE_SELECTORS, CLOSE_BUTTON_SELECTORS } from './ModalConstants';

/**
 * ModalFocusManager - Handles focus management, focus trapping, and close button focusing
 */
export class ModalFocusManager {
    private _modalElement: Element;
    private _focusedButton: HTMLElement | null = null;

    constructor(modalElement: Element) {
        this._modalElement = modalElement;
    }

    /**
     * Focus the close button for keyboard and screen reader users
     * Prioritizes the header close button (X) over footer buttons
     */
    public focusCloseButton(): void {
        // Use requestAnimationFrame for smoother timing after modal is shown
        requestAnimationFrame(() => {
            let closeButton: HTMLElement | null = null;
            let selectedSelector = '';

            for (const selector of CLOSE_BUTTON_SELECTORS) {
                closeButton = this._modalElement.querySelector(selector) as HTMLElement;
                if (closeButton) {
                    selectedSelector = selector;
                    console.log(`Found close button with selector: ${selector}`);
                    break;
                }
            }

            if (closeButton) {
                const isVisible = closeButton.offsetParent !== null &&
                    closeButton.offsetWidth > 0 &&
                    closeButton.offsetHeight > 0;

                if (isVisible) {
                    // Ensure it's focusable
                    closeButton.tabIndex = 0;

                    // Use another requestAnimationFrame to ensure DOM is stable
                    requestAnimationFrame(() => {
                        closeButton!.focus();
                        this._focusedButton = closeButton;
                        console.log(`Focused ${selectedSelector} close button`);

                        // Add focus trap to prevent focus from leaving the modal
                        this.setupFocusTrap();
                    });

                } else {
                    console.log('Close button not visible yet');
                }
            } else {
                console.log('No close button found. Available buttons:',
                    Array.from(this._modalElement.querySelectorAll('button')).map(btn => ({
                        text: btn.textContent?.trim(),
                        className: btn.className,
                        location: btn.closest('.modal-header') ? 'header' :
                            btn.closest('.modal-footer') ? 'footer' : 'body'
                    })));
            }
        });
    }

    /**
     * Setup focus trap to maintain focus within the modal
     */
    public setupFocusTrap(): void {
        const focusableElements = this._modalElement.querySelectorAll(
            FOCUSABLE_SELECTORS
        ) as NodeListOf<HTMLElement>;

        if (focusableElements.length === 0) return;

        const firstFocusable = focusableElements[0];
        const lastFocusable = focusableElements[focusableElements.length - 1];

        const trapFocus = (e: Event) => {
            const keyEvent = e as KeyboardEvent;
            if (keyEvent.key === 'Tab') {
                if (keyEvent.shiftKey) { // Shift + Tab
                    if (document.activeElement === firstFocusable) {
                        keyEvent.preventDefault();
                        lastFocusable.focus();
                    }
                } else { // Tab
                    if (document.activeElement === lastFocusable) {
                        keyEvent.preventDefault();
                        firstFocusable.focus();
                    }
                }
            }
        };

        this._modalElement.addEventListener('keydown', trapFocus);

        // Store the trap function for cleanup
        (this._modalElement as any)._focusTrapHandler = trapFocus;
    }

    /**
     * Remove focus trap when modal is hidden
     */
    public removeFocusTrap(): void {
        const trapHandler = (this._modalElement as any)._focusTrapHandler;
        if (trapHandler) {
            this._modalElement.removeEventListener('keydown', trapHandler);
            delete (this._modalElement as any)._focusTrapHandler;
        }
        this._focusedButton = null;
    }

    /**
     * Clean up and dispose of the manager
     */
    public dispose(): void {
        this.removeFocusTrap();
        this._focusedButton = null;
    }
}